import { Company } from '@/context/types';
import { BaseFilter, PaginationResponse } from '@/hooks/usePaginationFilter';
import {
  getAllOrganizations,
  getOrganizationById,
  onboardOrganization,
  updateOrganization,
} from '@/services/Organizations/organization.service';
import { CompanyTypes, User } from '@/types';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
type PaginationFilter<T> = T extends BaseFilter ? T : never;

export type OrganizationQuery = {
  id: string;
  filter?: PaginationFilter<OrgFilter>;
};

export enum CompaniesCount {
  ALL = 'all',
  TEN = '0-10',
  FIFTY = '11-50',
  HUNDRED = '51-100',
  HUNDRED_PLUS = '100+',
}

export enum SortByOrgFilter {
  NO_OF_COMPANIES = 'noOfCompanies',
  TRANSACTION_COUNT = 'transactionCount',
  TOTAL_PAYMENT_VALUE = 'totalPaymentValue',
}

export enum SortOrder {
  ASC = 1,
  DESC = -1,
}

export type OrgFilter = {
  search?: string;
  type?: CompanyTypes;
  limit: number;
  offset: number;
  organizationId?: string;
  companiesCount?: CompaniesCount;
  sortBy?: SortByOrgFilter;
  sortOrder?: SortOrder;
};

export type OnboardOrganizationData = {
  owner: { image?: any } & Partial<User>;
  company: Partial<Company>;
};

export type OrganizationResponse = Company & {
  totalPaymentVolume: number;
  totalTransaction: number;
  numberOfCompanies: number;
  currency?: string;
};

export const organizationsSlice = createApi({
  reducerPath: 'organizations',
  tagTypes: ['organizations', 'organization'],
  baseQuery: fetchBaseQuery(),
  endpoints: (builder) => ({
    getAllOrganization: builder.query<PaginationResponse<OrganizationResponse>, OrganizationQuery>({
      queryFn: async ({ id, filter }) => {
        const data = await getAllOrganizations({ id, filter });
        return { data };
      },
      providesTags: [{ type: 'organizations' }],
    }),
    getOrganizationById: builder.query<Company, { id: string; organizationId: string }>({
      queryFn: async ({ id }) => {
        const data = await getOrganizationById(id);
        return { data };
      },
      providesTags: [{ type: 'organization' }],
    }),
    update: builder.mutation<Company, { id: string; body: Partial<Company> }>({
      queryFn: async (data) => {
        try {
          const updatedData = await updateOrganization(data);
          return { data: updatedData };
        } catch (err: any) {
          return {
            error: {
              status: err.status,
              data: err.data,
              message: err.message,
            },
          };
        }
      },
      invalidatesTags: [{ type: 'organization' }],
    }),
    onboardOrganization: builder.mutation<Company, OnboardOrganizationData>({
      queryFn: async (body) => {
        const data = await onboardOrganization(body);
        return { data };
      },
    }),
  }),
});

export const {
  useGetAllOrganizationQuery,
  useGetOrganizationByIdQuery,
  useUpdateMutation,
  useOnboardOrganizationMutation,
} = organizationsSlice;
